import BigNumber from 'bignumber.js'

const INFINOX_LIVE_SERVER = 'InfinoxLimited-MT5Live'
const INFINOX_SPREAD_POINT_OFFSET = 0

export const isInfinoxLimitedMt5Live = (value: string | undefined | null): boolean =>
  String(value ?? '').trim() === INFINOX_LIVE_SERVER

const getValidDigits = (digits: number | undefined): number | null => {
  if (digits == null || !Number.isFinite(digits) || digits < 0) return null
  return Math.floor(digits)
}

const adjustPrice = (
  price: string,
  digits: number,
  direction: 'minus' | 'plus',
): string => {
  const bn = new BigNumber(price)
  if (bn.isNaN() || !bn.isFinite()) return price

  const pointOffset = new BigNumber(INFINOX_SPREAD_POINT_OFFSET).shiftedBy(-digits)
  const adjusted = direction === 'plus' ? bn.plus(pointOffset) : bn.minus(pointOffset)
  return adjusted.toFixed(digits, BigNumber.ROUND_HALF_UP)
}

const getValidTickSize = (tickSize: string | undefined): BigNumber | null => {
  const bn = new BigNumber(tickSize ?? '')
  if (bn.isNaN() || !bn.isFinite() || bn.lte(0)) return null
  return bn
}

const adjustCandlePrice = (price: string, pointOffset: BigNumber, tickDecimals: number): string => {
  const bn = new BigNumber(price)
  if (bn.isNaN() || !bn.isFinite()) return price
  const decimals = Math.max(bn.decimalPlaces() ?? 0, tickDecimals)
  return bn.minus(pointOffset).toFixed(decimals, BigNumber.ROUND_HALF_UP)
}

interface ApplyTickerPriceAdjustmentParams {
  brokerId: string
  symbolMap: Record<string, MarketSymbolItem>
}

export const applyTickerPriceAdjustment = (
  incoming: Record<string, WsTickerItem>,
  { brokerId, symbolMap }: ApplyTickerPriceAdjustmentParams,
): Record<string, WsTickerItem> => {
  if (INFINOX_SPREAD_POINT_OFFSET <= 0) return incoming

  const shouldAdjustBySession = isInfinoxLimitedMt5Live(brokerId)
  let changed = false
  const adjusted: Record<string, WsTickerItem> = {}

  for (const [symbol, item] of Object.entries(incoming)) {
    const symbolInfo = symbolMap[item.sym] ?? symbolMap[symbol]
    const digits = getValidDigits(symbolInfo?.digits)

    if (!shouldAdjustBySession || digits == null) {
      adjusted[symbol] = item
      continue
    }

    changed = true
    adjusted[symbol] = {
      ...item,
      bid: adjustPrice(item.bid, digits, 'plus'),
      ask: adjustPrice(item.ask, digits, 'minus'),
    }
  }

  return changed ? adjusted : incoming
}

interface ApplyCandlePriceAdjustmentParams {
  brokerId: string
  symbolInfo: MarketSymbolItem | undefined
}

export const applyCandlePriceAdjustment = (
  msg: WsCandleUpdateMessage,
  { brokerId, symbolInfo }: ApplyCandlePriceAdjustmentParams,
): WsCandleUpdateMessage => {
  if (INFINOX_SPREAD_POINT_OFFSET <= 0) return msg
  if (!isInfinoxLimitedMt5Live(brokerId)) return msg

  const tickSize = getValidTickSize(symbolInfo?.tickSize)
  if (!tickSize) return msg

  const pointOffset = tickSize.multipliedBy(INFINOX_SPREAD_POINT_OFFSET)
  const tickDecimals = tickSize.decimalPlaces() ?? 0

  return {
    ...msg,
    candle: {
      ...msg.candle,
      o: adjustCandlePrice(msg.candle.o, pointOffset, tickDecimals),
      h: adjustCandlePrice(msg.candle.h, pointOffset, tickDecimals),
      l: adjustCandlePrice(msg.candle.l, pointOffset, tickDecimals),
      c: adjustCandlePrice(msg.candle.c, pointOffset, tickDecimals),
      sp: msg.candle.sp + INFINOX_SPREAD_POINT_OFFSET * 2,
    },
  }
}
